import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { Database, Question } from '../types/database'
import { Sidebar } from './Sidebar'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type QuestionWithRelations = Question & {
    question_groups: {
        name: string
        books: {
            name: string
        }
    } | null
}

// Sortable Question Item Component
interface SortableQuestionItemProps {
    question: QuestionWithRelations
    index: number
    onMoveUp: (index: number) => void
    onMoveDown: (index: number) => void
    onRemove: (questionId: number) => void
    isFirst: boolean
    isLast: boolean
}

function SortableQuestionItem({
    question,
    index,
    onMoveUp,
    onMoveDown,
    onRemove,
    isFirst,
    isLast,
}: SortableQuestionItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: question.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm ${isDragging ? 'z-50' : ''
                }`}
        >
            <button
                type="button"
                className="flex-none cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                {...attributes}
                {...listeners}
            >
                <span className="material-symbols-outlined text-lg">drag_indicator</span>
            </button>

            <span className="flex-none flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-bold text-gray-600">
                {index + 1}
            </span>

            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{question.name}</p>
            </div>

            <div className="flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => onMoveUp(index)}
                    disabled={isFirst}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                    <span className="material-symbols-outlined text-lg">arrow_upward</span>
                </button>
                <button
                    type="button"
                    onClick={() => onMoveDown(index)}
                    disabled={isLast}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                    <span className="material-symbols-outlined text-lg">arrow_downward</span>
                </button>
                <button
                    type="button"
                    onClick={() => onRemove(question.id)}
                    className="p-1 text-gray-400 hover:text-red-600 ml-1"
                >
                    <span className="material-symbols-outlined text-lg">delete</span>
                </button>
            </div>
        </div>
    )
}

export function LearningSessionForm() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = Boolean(id)

    const [name, setName] = useState('')
    const [aiEnabled, setAiEnabled] = useState(true)
    const [status, setStatus] = useState(0)

    const [allQuestions, setAllQuestions] = useState<QuestionWithRelations[]>([])
    const [selectedQuestions, setSelectedQuestions] = useState<QuestionWithRelations[]>([])
    const [searchQuery, setSearchQuery] = useState('')

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
            const oldIndex = selectedQuestions.findIndex(q => q.id === active.id)
            const newIndex = selectedQuestions.findIndex(q => q.id === over.id)

            setSelectedQuestions(arrayMove(selectedQuestions, oldIndex, newIndex))
        }
    }

    useEffect(() => {
        if (id) {
            fetchInitialData(parseInt(id))
        } else {
            fetchInitialData(undefined)
        }
    }, [id])

    const fetchInitialData = async (sessionId?: number) => {
        try {
            setLoading(true)

            // Fetch all questions with hierarchy info (paginate 300 at a time to avoid Supabase limits)
            const PAGE_SIZE = 300
            let allQuestionsData: QuestionWithRelations[] = []
            let page = 0
            let hasMore = true

            while (hasMore) {
                const from = page * PAGE_SIZE
                const to = from + PAGE_SIZE - 1

                const { data, error } = await supabase
                    .from('questions')
                    .select(`
              *,
              question_groups (
                name,
                book_id,
                books (
                  name
                )
              )
            `)
                    .order('created_at', { ascending: false })
                    .range(from, to)

                if (error) throw error

                if (data && data.length > 0) {
                    allQuestionsData = [...allQuestionsData, ...data]
                }

                hasMore = data?.length === PAGE_SIZE
                page++
            }

            setAllQuestions(allQuestionsData)

            // If editing, fetch session details and its questions
            if (sessionId) {
                const { data: sessionData, error: sessionError } = await supabase
                    .from('learning_sessions')
                    .select('*')
                    .eq('id', sessionId)
                    .single()

                if (sessionError) throw sessionError

                setName(sessionData.name)
                setAiEnabled(sessionData.ai_enabled)
                setStatus(sessionData.status)

                const { data: sessionQuestionsData, error: sessionQuestionsError } = await supabase
                    .from('learning_session_questions')
                    .select(`
            question_id,
            questions (
              *,
              question_groups (
                name,
                book_id,
                books (
                  name
                )
              )
            )
          `)
                    .eq('session_id', sessionId)
                    .order('display_order', { ascending: true })

                if (sessionQuestionsError) throw sessionQuestionsError

                // Map back to QuestionWithRelations structure
                // The type assertion is needed because the nested structure from query is slightly different in deep types
                const mappedQuestions = (sessionQuestionsData || []).map((item: any) => item.questions)
                setSelectedQuestions(mappedQuestions)
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data')
        } finally {
            setLoading(false)
        }
    }

    const handleAddQuestion = (question: QuestionWithRelations) => {
        if (selectedQuestions.length >= 20) {
            alert('최대 20개까지만 선택할 수 있습니다.')
            return
        }
        if (selectedQuestions.find(q => q.id === question.id)) {
            return // Already added
        }
        setSelectedQuestions([...selectedQuestions, question])
    }

    const handleRemoveQuestion = (questionId: number) => {
        setSelectedQuestions(selectedQuestions.filter(q => q.id !== questionId))
    }

    const handleMoveUp = (index: number) => {
        if (index === 0) return
        const newQuestions = [...selectedQuestions]
        const temp = newQuestions[index]
        newQuestions[index] = newQuestions[index - 1]
        newQuestions[index - 1] = temp
        setSelectedQuestions(newQuestions)
    }

    const handleMoveDown = (index: number) => {
        if (index === selectedQuestions.length - 1) return
        const newQuestions = [...selectedQuestions]
        const temp = newQuestions[index]
        newQuestions[index] = newQuestions[index + 1]
        newQuestions[index + 1] = temp
        setSelectedQuestions(newQuestions)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name.trim()) {
            setError('세션 이름을 입력해주세요.')
            return
        }
        if (selectedQuestions.length === 0) {
            setError('최소 1개의 문제를 선택해주세요.')
            return
        }

        try {
            setSaving(true)
            let sessionId = id ? parseInt(id) : undefined

            if (isEditMode && sessionId) {
                // Update session
                const { error: updateError } = await supabase
                    .from('learning_sessions')
                    .update({
                        name,
                        ai_enabled: aiEnabled,
                        status: status
                    })
                    .eq('id', sessionId)

                if (updateError) throw updateError
            } else {
                // Create session
                const { data: newSession, error: createError } = await supabase
                    .from('learning_sessions')
                    .insert({
                        name,
                        ai_enabled: aiEnabled,
                        status: status
                    })
                    .select()
                    .single()

                if (createError) throw createError
                sessionId = newSession.id
            }

            // Update questions
            // First delete existing links
            const { error: deleteError } = await supabase
                .from('learning_session_questions')
                .delete()
                .eq('session_id', sessionId)

            if (deleteError) throw deleteError

            // Insert new links
            const linksToInsert = selectedQuestions.map((q, index) => ({
                session_id: sessionId!,
                question_id: q.id,
                display_order: index
            }))

            const { error: insertError } = await supabase
                .from('learning_session_questions')
                .insert(linksToInsert)

            if (insertError) throw insertError

            navigate('/learning-sessions')
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save session')
        } finally {
            setSaving(false)
        }
    }

    const filteredQuestions = allQuestions.filter(q =>
        !selectedQuestions.find(sq => sq.id === q.id) && // Exclude already selected
        (q.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            q.question_groups?.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    )

    if (loading) {
        return (
            <div className="relative flex min-h-screen w-full bg-background-light font-display">
                <Sidebar />
                <main className="ml-64 flex flex-1 items-center justify-center">
                    <div className="flex items-center gap-2 text-gray-600">
                        <span className="material-symbols-outlined animate-spin">progress_activity</span>
                        <span className="text-lg">로딩 중...</span>
                    </div>
                </main>
            </div>
        )
    }

    return (
        <div className="relative flex min-h-screen w-full bg-background-light font-display">
            <Sidebar />
            <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8 overflow-hidden h-screen">
                <div className="w-full max-w-7xl mx-auto flex flex-col h-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                        <h1 className="text-xl font-bold text-gray-900">
                            {isEditMode ? '학습 세션 수정' : '새 학습 세션 만들기'}
                        </h1>
                        <button
                            onClick={() => navigate('/learning-sessions')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    <div className="flex flex-1 overflow-hidden">
                        {/* Main Form Content */}
                        <div className="flex flex-1 gap-6 p-6 overflow-hidden">

                            {/* Left Column: available questions */}
                            <div className="flex-1 flex flex-col min-w-0 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                <div className="p-4 border-b border-gray-200 bg-white">
                                    <h3 className="font-medium text-gray-700 mb-2">문제 선택</h3>
                                    <div className="relative">
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                                            <span className="material-symbols-outlined text-lg">search</span>
                                        </span>
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary"
                                            placeholder="문제 이름 또는 그룹 이름으로 검색..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                                    {filteredQuestions.map((question) => (
                                        <div
                                            key={question.id}
                                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-primary cursor-pointer transition-colors group"
                                            onClick={() => handleAddQuestion(question)}
                                        >
                                            <div className="min-w-0 flex-1 mr-3">
                                                <p className="text-sm font-medium text-gray-900 truncate">{question.name}</p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {question.question_groups?.books?.name} &rsaquo; {question.question_groups?.name}
                                                </p>
                                            </div>
                                            <button className="text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                                <span className="material-symbols-outlined">add_circle</span>
                                            </button>
                                        </div>
                                    ))}
                                    {filteredQuestions.length === 0 && (
                                        <div className="text-center py-8 text-gray-500 text-sm">
                                            검색 결과가 없습니다.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column: session details & selected questions */}
                            <div className="w-1/2 flex flex-col min-w-0 bg-white">
                                <div className="mb-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">세션 이름</label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                                            placeholder="예: 2025-12-31-part1"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                checked={aiEnabled}
                                                onChange={(e) => setAiEnabled(e.target.checked)}
                                            />
                                            <span className="text-sm font-medium text-gray-700">AI 사용 허용</span>
                                        </label>

                                        <label className="flex items-center gap-2 cursor-pointer ml-6">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                checked={status > 0}
                                                onChange={(e) => setStatus(e.target.checked ? 1 : 0)}
                                            />
                                            <span className="text-sm font-medium text-gray-700">공개(Publish)</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="flex-1 flex flex-col min-h-0 border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="p-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                                        <h3 className="font-medium text-gray-700">선택된 문제 ({selectedQuestions.length}/20)</h3>
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-gray-50">
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={selectedQuestions.map(q => q.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {selectedQuestions.map((question, index) => (
                                                    <SortableQuestionItem
                                                        key={question.id}
                                                        question={question}
                                                        index={index}
                                                        onMoveUp={handleMoveUp}
                                                        onMoveDown={handleMoveDown}
                                                        onRemove={handleRemoveQuestion}
                                                        isFirst={index === 0}
                                                        isLast={index === selectedQuestions.length - 1}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                        {selectedQuestions.length === 0 && (
                                            <div className="text-center py-12 text-gray-400 text-sm">
                                                왼쪽 목록에서 문제를 선택하세요.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
                        {error && (
                            <div className="flex-1 flex items-center text-red-600 text-sm mr-4">
                                <span className="material-symbols-outlined text-lg mr-1">error</span>
                                {error}
                            </div>
                        )}
                        <button
                            onClick={() => navigate('/learning-sessions')}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            취소
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={saving || selectedQuestions.length === 0}
                            className="px-4 py-2 text-sm font-bold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                        >
                            {saving && <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>}
                            {saving ? '저장 중...' : '저장하기'}
                        </button>
                    </div>
                </div>
            </main>
        </div>
    )
}
